<script setup lang="ts">
import MiiaFwAuthModal from './AuthModal.vue'
import MiiaFwAlertMessageLight from './AlertMessageLight.vue'
import { useLoadingView } from '../composables/useLoadingView'
import { authChallenge } from '../utils/miiaFwCognito'
import MiiaFwConfig from '@/configs/config'
import { reactive, ref, useTemplateRef } from 'vue'
import type { AuthInfo } from '../types/cognito/auth'
/**
 * @module components/miiafw/auth/MiiaFwAuthChallenge
 * @description MiiaFWのアクティベーション機能
 * @vue-data {Boolean} [myModal = false] モーダル制御。trueのときモーダルを開く
 * @vue-data {String} [userName = 空白] モーダル内に表示させるユーザID
 * @vue-data {String} [verificationCode = 空白] 認証コード
 * @vue-data {Boolean} [vcHasError = false] 認証コードのエラーの有無。trueのときエラー有り
 */
const submit = useTemplateRef<HTMLButtonElement>('submit')
const alertMessageLight =
  useTemplateRef<InstanceType<typeof MiiaFwAlertMessageLight>>('alertMessageLight')
const mfaCodeInput = useTemplateRef<HTMLInputElement>('mfaCodeInput')

const emit = defineEmits<{ 'close-top': []; 'open-password-update': [] }>()

const { setLoadingView } = useLoadingView()

const myModal = ref(false)
let challengeName = ''
let session = ''
let userName = ''
const mfaCode = ref('')
const alertView = reactive<{ [keys: string]: { hasError: boolean; alertMessage: string } }>({
  mc: { hasError: false, alertMessage: '' }
})

/** エラーメッセージを初期化 */
const resetAlertView = () => {
  Object.keys(alertView).forEach(key => {
    alertView[key].hasError = false
    alertView[key].alertMessage = ''
  })
}

/**
 * ユーザIDが入った状態のモーダルを開く
 * @param argChallengeName MFA認証の種類
 * @param argSession セッション
 * @param argUserName ユーザーID
 */
const openModal = (argChallengeName: string, argSession: string, argUserName: string) => {
  if (
    argChallengeName !== undefined &&
    argChallengeName.length > 0 &&
    argSession !== undefined &&
    argSession.length > 0 &&
    argUserName !== undefined &&
    argUserName.length > 0
  ) {
    challengeName = argChallengeName
    session = argSession
    userName = argUserName
  }
  myModal.value = true
}

const doInitialize = () => {
  challengeName = ''
  session = ''
  userName = ''
  mfaCode.value = ''
  resetAlertView()
}

/**
 * 一番上のモーダルを閉じる。＜MiiaFwAuthModal＞タグを指定することで、特定の操作に対応付けることができる
 */
const closeModal = () => {
  doInitialize()
  myModal.value = false
  emit('close-top')
}

/**
 * モーダル上でTab操作をした際、モーダル上のオブジェクトを循環できるようにする
 * @param e キーボードイベント
 */
const firstTab = (e: KeyboardEvent) => {
  e.preventDefault()
  e.stopPropagation()
  submit.value?.focus()
}

/**
 * モーダル上でShift+Tab操作をした際、モーダル上のオブジェクトを循環できるようにする
 * @param e キーボードイベント
 */
const lastTab = (e: KeyboardEvent) => {
  // Shift + Tabは、Tabの遡りなのでデフォルトのまま
  if (!e.shiftKey) {
    e.preventDefault()
    e.stopPropagation()
    mfaCodeInput.value?.focus()
  }
}

/**
 * 認証コードのエラー表示機能。表示されているエラーを初期化し、各項目に対してバリデーションチェックを行う。問題が無かったらmodule:miiafw/mixin/MiiaFwCognitoMixin#miiafwCognitolibs_activateへユーザIDと認証コードを渡し、モーダルを閉じる
 */
const doSubmit = () => {
  // エラー表示初期化
  resetAlertView()
  alertMessageLight.value?.deleteAlertMessage()

  // バリデーションチェック
  if (mfaCode.value.length < 1) {
    alertView.mc.hasError = true
    alertView.mc.alertMessage = '検証コードは必須項目です'
  } else {
    // 処理中表示
    setLoadingView(true)
    // アクティベーション処理
    authChallenge(challengeName, session, userName, mfaCode.value)
      .then(result => {
        if (MiiaFwConfig.config.authorization.expirationTime === Number.MAX_VALUE) {
          // 認証成功(有効期限チェックなし)
          setLoadingView(false) // 必ずコンポーネント消滅前に実行
          closeModal()
        } else {
          // パスワード有効期限チェック
          if ((result as AuthInfo).isExpired) {
            // 【要検討】有効期限切れフラグを有効化(パスワード更新画面表示時にブラウザ更新されたら強制サインアウトするため)
            setLoadingView(false) // 必ずコンポーネント消滅前に実行
            doInitialize()
            emit('open-password-update')
          } else {
            // 認証成功
            setLoadingView(false) // 必ずコンポーネント消滅前に実行
            closeModal()
          }
        }
      })
      .catch(error => {
        alertView.mc.hasError = true
        alertMessageLight.value?.addAlertMessage(error.message)
        setLoadingView(false) // 必ずコンポーネント消滅前に実行
      })
  }
}

defineExpose({
  // data
  myModal,
  mfaCode,
  alertView,

  // methods
  openModal,
  closeModal,
  firstTab,
  lastTab,
  doSubmit
})
</script>

<template>
  <!-- @closeを指定すると枠外クリック時にモーダルウィンドウを閉じる。 -->
  <!-- <MyModal @close="closeModal" v-if="modal"> -->
  <MiiaFwAuthModal v-if="myModal" @click-close="closeModal">
    <!-- default スロットコンテンツ -->
    <h4 style="font-size: 1.1rem; font-weight: 500">MFA認証</h4>
    <div style="padding: 30px 10px 30px 10px; margin-bottom: 10px; border: 1px solid #bcbcbc">
      <div
        class="form-group"
        style="text-align: left"
        v-bind:class="{ 'has-error': alertView.mc.hasError }"
      >
        <label class="control-label">
          検証コード
          <span v-if="alertView.mc.alertMessage.length > 0" class="ff-alert-msg">
            <font-awesome-icon icon="exclamation-circle" />{{ alertView.mc.alertMessage }}
          </span>
        </label>
        <input
          ref="mfaCodeInput"
          v-model="mfaCode"
          class="form-control"
          tabindex="1"
          v-focus
          @keydown.shift.tab="firstTab"
        />
      </div>
      <p>※携帯電話のSMSメッセージをご確認ください。</p>
      <div style="text-align: right">
        <button
          ref="submit"
          class="btn ff-btn-primary"
          tabindex="2"
          @click="doSubmit"
          @keydown.tab="lastTab"
        >
          &nbsp;検証コード送信
        </button>
      </div>
    </div>
    <MiiaFwAlertMessageLight ref="alertMessageLight"></MiiaFwAlertMessageLight>
    <!-- /default -->
    <!-- footer スロットコンテンツ -->
    <template #slot> </template>
  </MiiaFwAuthModal>
</template>
